/**
 * Chat boxes over the heads of people on a break.
 *
 * ── Why every line of this is in the renderer ───────────────────────────────
 *
 * `sim/systems/villagers.ts` argues the general case and this is a direct
 * application of it: **where a villager goes is simulation, how he looks having
 * got there is decoration.** A speech bubble is the purest case of the second
 * kind. Nothing here writes to the world, nothing accumulates across frames
 * that matters, `hashWorld` does not move, `SAVE_VERSION` does not move, and
 * turning the whole file off changes no outcome — which is the test.
 *
 * The lines themselves are content and live in `src/data/chatter.ts`. This file
 * is the only place the renderer's `ErrandId` vocabulary and that file's `Mood`
 * vocabulary meet, which is deliberate: `src/data` may not import from
 * `src/sim/systems`, so mirroring `Errand` into the data layer would have put a
 * second authority for the same six numbers in the codebase. One translation
 * function, here, instead.
 *
 * ── Why it costs no state ───────────────────────────────────────────────────
 *
 * The same argument `errandOf` makes, and it has to be, because a bubble that
 * remembered anything would be a bubble that a loaded save got wrong. Who is
 * speaking, when, and which sentence are **pure functions of the villager's
 * slot, the tick and `w.raceId`** — all three already hashed. Two worlds with
 * the same hash say the same things at the same moments, for ever, and a world
 * loaded mid-session says what a world run from the start would have said.
 *
 * Every hash here takes **its own salt**, for the reason the four separate
 * `Rng`s in the simulation exist: a shared stream means a new consumer silently
 * retunes every system downstream of it. `errandOf` uses `0x51ed`; nothing here
 * goes near it.
 *
 * ── The cost, and where the cap is ──────────────────────────────────────────
 *
 * `bubbleFor` is three hashes and a compare, and it is called only for
 * villagers the view cull has already kept — a few hundred at most, against
 * nineteen thousand off screen paying nothing. What is genuinely expensive is
 * the *drawing*: a Pixi `Text` re-rasterises whenever its string changes, so
 * the pool is capped at `MAX_BUBBLES` and each slot remembers the string it is
 * already showing and skips the assignment when it has not moved. A line lasts
 * `SPEAK_TICKS` — a little over three seconds — so a full pool re-rasterises
 * about four times a second between them, not sixty.
 */

import { Container, Graphics, Text, type TextStyle } from 'pixi.js';
import { Mouth, linesFor, type Mood, type MouthId } from '../data/chatter.ts';
import { Errand, type ErrandId } from '../sim/systems/villagers.ts';
import { UnitState } from '../sim/types.ts';
import type { World } from '../sim/world.ts';
import { C } from '../data/palette.ts';
import { hash2 } from '../core/math.ts';

export { Mouth, type MouthId };

/** Salts. One each, and none of them `errandOf`'s. */
const SALT_MOUTH = 0x2b19;
const SALT_SPEAK = 0x7c41;
const SALT_WHEN = 0x1d63;
const SALT_LINE = 0x4f8b;

/**
 * How often a villager gets the chance to say something, and for how long.
 *
 * ── Why each villager has his own clock rather than the errand's ────────────
 *
 * The obvious construction is to hang the sentence off `errandOf`'s own phase
 * — one errand, one line, a hashed window inside it — and it is wrong in a way
 * that only shows up on screen. A window drawn as `start ∈ [0, PERIOD − LEN)`
 * and tested against `tick % PERIOD` does not put a constant fraction of the
 * town mid-sentence: at `tick % PERIOD === 0` the only people talking are the
 * ones who drew `start === 0`, and at the middle of the period it is nearly
 * everybody. The town would fall silent all at once every eight seconds and
 * then all start talking at once, which reads as a bug because it is one. The
 * first cut of this file did exactly that and a test caught it at 5 speakers
 * where it expected 300.
 *
 * So each villager carries a hashed **offset** into a cycle of his own and the
 * clock is continuous. At any tick, `SPEAK_TICKS / SPEAK_PERIOD` of the town is
 * mid-sentence — uniformly, for ever — and no sentence is ever cut short by a
 * period boundary, because there is no shared boundary to be cut by.
 *
 * 256 ticks is 8.5s of simulated settlement time and 96 is 3.2s of it. The
 * period matches `ERRAND_TICKS` in `villagers.ts` by eye rather than by import:
 * tying the length of a sentence to the length of an errand for ever is not a
 * relationship either file wants to promise.
 */
const SPEAK_PERIOD = 256;
const SPEAK_TICKS = 96;

/**
 * How often somebody on this errand has something to say, per errand period.
 *
 * Work is low on purpose. A man swinging a tool who talks as much as a man
 * stood at the well reads as a town where nobody is doing anything, and the
 * brief asked for the bubbles to mean *idle, taking a break*.
 */
const SPEAK_CHANCE: Record<Mood, number> = {
  talk: 0.85,
  gather: 0.5,
  rest: 0.4,
  pray: 0.35,
  work: 0.14,
  haul: 0.3,
};

/**
 * Which mouth this villager has.
 *
 * Stable for the life of the slot and derived from the slot alone, so a man
 * does not become a woman when the camera pans. When a slot is recycled it is a
 * different person and may well be a different one — which is correct.
 *
 * Exported beyond the bubbles because the sprite sheets want it too: the art
 * pipeline bakes two villager bodies, and this is the one authority on which of
 * them a given slot wears. Two authorities for that would be a man whose body
 * and whose voice disagreed.
 */
export function mouthOf(i: number): MouthId {
  return hash2(i, 0, SALT_MOUTH) < 0.5 ? Mouth.Man : Mouth.Woman;
}

/**
 * The errand vocabulary translated into the content vocabulary.
 *
 * Returns null where there is nothing to say: walking somewhere is not an
 * occasion for a sentence, and neither is trudging to a job you have not
 * reached. `Errand.Wander` is deliberately silent — a man walking with no
 * destination anybody could name is the one villager in the town who is not
 * talking to anybody.
 */
export function moodOf(errand: ErrandId, state: number, carrying = false): Mood | null {
  /**
   * A full basket outranks the errand, and it is the one line that is *not*
   * about being idle.
   *
   * The `haul` bank was authored before there was anything to hang it on — the
   * node system landed in the same batch — and this is the hook it was waiting
   * for. It comes first for the same reason `folk.ts` draws the load whatever
   * the errand: the walk home is the part of the trip worth watching, and a man
   * complaining about the straps while carrying nothing would be a lie.
   */
  if (carrying) return 'haul';
  if (errand === Errand.Work) return state === UnitState.Working ? 'work' : null;
  if (errand === Errand.Talk) return 'talk';
  if (errand === Errand.Gather) return 'gather';
  if (errand === Errand.Rest) return 'rest';
  if (errand === Errand.Pray) return 'pray';
  return null;
}

export interface Bubble {
  text: string;
  mouth: MouthId;
  /** 0 at the edges of the window, 1 in the middle. Drives the fade. */
  weight: number;
}

/**
 * What this villager is saying right now, or null.
 *
 * Pure. Three hashes on the fast path, one more on the slow one, and the first
 * of them rejects roughly half of everybody before any of the rest run.
 */
export function bubbleFor(w: World, i: number, mood: Mood): Bubble | null {
  // His own clock: offset by a hash of his slot, so the town's mouths are
  // spread evenly through the cycle rather than sharing a boundary.
  const clock = w.tick + ((SPEAK_PERIOD * hash2(i, 0, SALT_WHEN)) | 0);
  const into = clock % SPEAK_PERIOD;
  if (into >= SPEAK_TICKS) return null;

  // Which time round: the line and the coin flip are per cycle, not per tick.
  const cycle = (clock / SPEAK_PERIOD) | 0;
  if (hash2(i, cycle, SALT_SPEAK) >= SPEAK_CHANCE[mood]) return null;

  const mouth = mouthOf(i);
  const lines = linesFor(w.raceId, mood, mouth);
  if (lines.length === 0) return null;

  // Triangular, so a line fades up and out rather than snapping. The 0.18
  // shoulder is under a fifth of the window at each end.
  const f = into / SPEAK_TICKS;
  const weight = Math.min(1, Math.min(f, 1 - f) / 0.18);

  return { text: lines[(lines.length * hash2(i, cycle, SALT_LINE)) | 0], mouth, weight };
}

/** As many as may be on screen at once. See the cost note in the header. */
const MAX_BUBBLES = 2;

const PAD_X = 7;
const PAD_Y = 4;
const TAIL = 5;

/**
 * Two styles, because the two mouths are told apart by their type rather than
 * by a label. A woman's line is set a shade lighter and a half-point smaller;
 * the difference is small on purpose — it should read as two voices in a street
 * and never as two factions.
 */
const STYLE: Record<MouthId, Partial<TextStyle>> = {
  [Mouth.Man]: {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 11,
    fill: C.bone,
  },
  [Mouth.Woman]: {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 10.5,
    fontStyle: 'italic',
    fill: C.parchment,
  },
};

interface Slot {
  g: Graphics;
  t: Text;
  /** What this slot is already showing, so an unchanged line skips the raster. */
  shown: string;
  mouth: MouthId;
}

/**
 * The pool, and the layer it draws into.
 *
 * `begin` / `push` / `end` rather than a list-in, list-out call, because the
 * caller is already walking every unit on screen and building a second array of
 * a few hundred entries per frame to hand back would cost more than the whole
 * feature does.
 *
 * `push` is taken **in slot order**, which is the order the scene's unit loop
 * runs in, and that is the whole of the arbitration when more than
 * `MAX_BUBBLES` people are talking at once. It is a real bias toward low slot
 * indices — the oldest villagers in the town talk more visibly than the newest
 * — and it is chosen because it is *stable*: any rule that ranked by distance
 * or by hash would reshuffle as the camera moved and bubbles would flicker in
 * and out of a crowd that had not changed.
 */
export class ChatterBubbles {
  readonly layer = new Container();
  private readonly pool: Slot[] = [];
  private used = 0;
  /**
   * What is already on screen this frame, so the same sentence is never up
   * twice at once.
   *
   * This is not fussiness — the first shot of the feature had *"They counted us
   * twice and fed us once."* over two different heads a hundred pixels apart,
   * which is the one thing that turns a crowd back into a spawner. It is
   * unavoidable at the bank sizes involved (a dozen lines against fourteen
   * boxes) and it is much better fixed here than by writing three hundred
   * lines: a suppressed bubble simply does not appear, which reads as that man
   * having nothing to say.
   *
   * A plain array rather than a `Set`, because it holds at most `MAX_BUBBLES`
   * and is reused across frames — a linear scan of fourteen strings beats
   * allocating a hash set sixty times a second.
   */
  private readonly showing: string[] = [];
  /** Boxes already placed this frame, so two of them cannot land on each other. */
  private readonly placed: Array<{ x0: number; y0: number; x1: number; y1: number }> = [];

  begin(): void {
    this.used = 0;
    this.showing.length = 0;
    this.placed.length = 0;
  }

  /** `sx`/`sy` are screen coordinates of the speaker's head. */
  push(sx: number, sy: number, b: Bubble): void {
    if (this.used >= MAX_BUBBLES) return;
    if (this.showing.includes(b.text)) return;
    this.showing.push(b.text);
    const s = this.slot(this.used++);

    if (s.shown !== b.text || s.mouth !== b.mouth) {
      if (s.mouth !== b.mouth) {
        s.t.style = STYLE[b.mouth] as TextStyle;
        s.mouth = b.mouth;
      }
      s.t.text = b.text;
      s.shown = b.text;
      this.frame(s);
    }

    const w = s.t.width + PAD_X * 2;
    const h = s.t.height + PAD_Y * 2;
    const x = sx - w / 2;
    const y = sy - h - TAIL;

    /**
     * Two bubbles on top of each other are worse than one bubble.
     *
     * A crowd stands close together, so two people talking a tile apart put
     * their boxes in the same place and the reader gets half of each sentence —
     * which the first shot of the feature showed happening between "She was on
     * the north gate" and the line beside it. The later of the two is dropped
     * rather than nudged: moving a bubble breaks the one thing a tail is for,
     * which is saying *whose* line it is.
     *
     * Tested after the text is set because a box cannot be measured before it
     * has something in it, so the slot is taken and then given straight back.
     * That is free — nothing was drawn — and it keeps the pool packed, because
     * `used` is what `end()` hides from.
     */
    for (const r of this.placed) {
      if (x < r.x1 && x + w > r.x0 && y < r.y1 && y + h > r.y0) {
        this.used--;
        this.showing.pop();
        s.g.visible = false;
        return;
      }
    }
    this.placed.push({ x0: x, y0: y, x1: x + w, y1: y + h });

    s.g.x = x;
    s.g.y = y;
    s.g.alpha = b.weight;
    s.g.visible = true;
  }

  end(): void {
    for (let k = this.used; k < this.pool.length; k++) this.pool[k].g.visible = false;
  }

  /** Redraw the box behind a line that has just changed length. */
  private frame(s: Slot): void {
    const w = s.t.width + PAD_X * 2;
    const h = s.t.height + PAD_Y * 2;
    s.g.clear();
    s.g.roundRect(0, 0, w, h, 3).fill({ color: C.ashDeep, alpha: 0.82 });
    // The tail, pointing down at the head it belongs to.
    s.g.moveTo(w / 2 - 4, h).lineTo(w / 2 + 4, h).lineTo(w / 2, h + TAIL).closePath();
    s.g.fill({ color: C.ashDeep, alpha: 0.82 });
    s.g.roundRect(0, 0, w, h, 3).stroke({ color: C.boneDeep, alpha: 0.45, width: 1 });
    s.t.x = PAD_X;
    s.t.y = PAD_Y;
  }

  private slot(k: number): Slot {
    let s = this.pool[k];
    if (s === undefined) {
      const g = new Graphics();
      const t = new Text({ text: '', style: STYLE[Mouth.Man] as TextStyle });
      g.addChild(t);
      this.layer.addChild(g);
      s = { g, t, shown: '', mouth: Mouth.Man };
      this.pool[k] = s;
    }
    return s;
  }
}
